// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import EmergencyFuelSystem from '../utils/game/EmergencyFuelSystem';
import './EmergencyFuelModal.css';

/**
 * Emergency Fuel Modal
 * Shows emergency options when low on fuel
 */
const EmergencyFuelModal = ({ 
  onClose, 
  currentLocation,
  allSystems,
  credits,
  power,
  onDistressBeacon,
  onSynthesizeFuel,
  onSolarCharge
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Check available options
  const distressResult = EmergencyFuelSystem.callDistressBeacon(currentLocation, allSystems, credits);
  const synthesisResult = EmergencyFuelSystem.synthesizeFuel(power);
  const nearestStar = EmergencyFuelSystem.findNearestStar(currentLocation, allSystems);
  const solarResult = EmergencyFuelSystem.checkSolarCharging(currentLocation, nearestStar);
  
  const handleDistressBeacon = async () => {
    if (!distressResult.success) return;
    
    setProcessing(true);
    await onDistressBeacon(distressResult);
    setProcessing(false);
    onClose();
  };
  
  const handleSynthesis = async () => {
    if (!synthesisResult.success) return;
    
    setProcessing(true);
    await onSynthesizeFuel(synthesisResult);
    setProcessing(false);
    onClose();
  };
  
  return createPortal(
    <div className="emergency-fuel-overlay" onClick={onClose}>
      <div className="emergency-fuel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="emergency-header">
          <span className="emergency-icon">⚠️</span>
          <h2>EMERGENCY FUEL OPTIONS</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="emergency-warning">
          Critical fuel shortage detected. Select an emergency option:
        </div>
        
        <div className="emergency-options">
          {/* Distress Beacon */}
          <div 
            className={`emergency-option ${!distressResult.success ? 'disabled' : ''} ${selectedOption === 'distress' ? 'selected' : ''}`}
            onClick={() => distressResult.success && setSelectedOption('distress')}
          >
            <div className="option-icon">📡</div>
            <div className="option-content">
              <h3>Distress Beacon</h3>
              <p>Call for emergency tow to nearest station</p>
              <div className="option-details">
                {distressResult.success ? (
                  <>
                    <div className="detail-row">
                      <span>Destination:</span>
                      <span className="highlight">{distressResult.destination.name}</span>
                    </div>
                    <div className="detail-row">
                      <span>Cost:</span>
                      <span className="cost">{distressResult.cost.toLocaleString()}¢</span>
                    </div>
                    <div className="detail-row">
                      <span>Time:</span>
                      <span>{distressResult.time}s</span>
                    </div>
                  </>
                ) : (
                  <div className="error-message">{distressResult.reason}</div>
                )}
              </div>
            </div>
          </div>
          
          {/* Fuel Synthesis */}
          <div 
            className={`emergency-option ${!synthesisResult.success ? 'disabled' : ''} ${selectedOption === 'synthesis' ? 'selected' : ''}`}
            onClick={() => synthesisResult.success && setSelectedOption('synthesis')}
          >
            <div className="option-icon">⚗️</div>
            <div className="option-content">
              <h3>Fuel Synthesis</h3>
              <p>Convert ship power to antimatter fuel</p>
              <div className="option-details">
                {synthesisResult.success ? (
                  <>
                    <div className="detail-row">
                      <span>Fuel Gained:</span>
                      <span className="highlight">+{synthesisResult.fuelGained}%</span>
                    </div>
                    <div className="detail-row">
                      <span>Power Cost:</span>
                      <span className="cost">-{synthesisResult.powerCost}%</span>
                    </div>
                    <div className="detail-row">
                      <span>Cooldown:</span>
                      <span>{synthesisResult.cooldown / 60} minutes</span>
                    </div>
                  </>
                ) : (
                  <div className="error-message">{synthesisResult.reason}</div>
                )}
              </div>
            </div>
          </div>
          
          {/* Solar Charging */}
          <div 
            className={`emergency-option ${!solarResult.available ? 'disabled' : ''} ${selectedOption === 'solar' ? 'selected' : ''}`}
            onClick={() => solarResult.available && setSelectedOption('solar')}
          >
            <div className="option-icon">☀️</div>
            <div className="option-content">
              <h3>Solar Charging</h3>
              <p>Harvest energy from nearby star</p>
              <div className="option-details">
                {solarResult.available ? (
                  <>
                    <div className="detail-row">
                      <span>Rate:</span>
                      <span className="highlight">{solarResult.rate}% per minute</span>
                    </div>
                    <div className="detail-row">
                      <span>Distance:</span>
                      <span>{solarResult.distance.toFixed(1)} LY</span>
                    </div>
                  </>
                ) : (
                  <div className="error-message">{solarResult.reason}</div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="emergency-actions">
          <button 
            className="emergency-btn cancel"
            onClick={onClose}
            disabled={processing}
          >
            CANCEL
          </button>
          <button 
            className="emergency-btn confirm"
            onClick={() => {
              if (selectedOption === 'distress') handleDistressBeacon();
              else if (selectedOption === 'synthesis') handleSynthesis();
            }}
            disabled={!selectedOption || processing}
          >
            {processing ? 'PROCESSING...' : 'ACTIVATE'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EmergencyFuelModal;
